import { useEffect, useState } from 'react';
import api from '../../../services/api';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { toast } from 'sonner';
import { Save, Gift } from 'lucide-react';

const DEFAULTS = {
  enabled: true,
  discount_percent: 15,
  title: 'Registrera dig & få 15% rabatt',
  body: 'Skapa ett konto idag och få en rabattkod direkt på din första beställning.',
  button_text: 'Få min rabattkod',
  code_prefix: 'VALKOMMEN',
  delay_ms: 4000,
  show_once_per_session: true,
  validity_days: 30,
};

const WelcomePopupSection = ({ getAuthHeaders }) => {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/admin/welcome-popup', { headers: getAuthHeaders() });
        setCfg({ ...DEFAULTS, ...(res.data || {}) });
      } catch {
        toast.error('Kunde inte hämta popup-inställningar');
      } finally {
        setLoading(false);
      }
    })();
  }, [getAuthHeaders]);

  const update = (k, v) => setCfg(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...cfg,
        discount_percent: parseInt(cfg.discount_percent, 10) || 0,
        delay_ms: parseInt(cfg.delay_ms, 10) || 0,
        validity_days: parseInt(cfg.validity_days, 10) || 0,
      };
      await api.put('/admin/welcome-popup', payload, { headers: getAuthHeaders() });
      toast.success('Popup-inställningar sparade');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-4 text-sm text-slate-400">Laddar...</div>;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="admin-welcome-popup-section">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
          <Gift className="w-5 h-5 text-[#2a9d8f]" />
          Välkomstpopup
        </h2>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => update('enabled', e.target.checked)}
            className="w-4 h-4"
            data-testid="welcome-popup-enabled-toggle"
          />
          <span className="text-sm font-medium text-slate-700">Aktiv</span>
        </label>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Rabattprocent</label>
          <Input
            type="number" min="0" max="100"
            value={cfg.discount_percent}
            onChange={(e) => update('discount_percent', e.target.value)}
            data-testid="welcome-popup-percent-input"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Giltighet (dagar)</label>
          <Input
            type="number" min="0" max="3650"
            value={cfg.validity_days}
            onChange={(e) => update('validity_days', e.target.value)}
            data-testid="welcome-popup-validity-input"
          />
          <p className="text-[11px] text-slate-400 mt-1">0 = ingen utgångsdatum. Annars utgår koden X dagar efter registrering.</p>
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Fördröjning innan visning (ms)</label>
          <Input
            type="number" min="0" step="500"
            value={cfg.delay_ms}
            onChange={(e) => update('delay_ms', e.target.value)}
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Rubrik</label>
        <Input
          value={cfg.title}
          onChange={(e) => update('title', e.target.value)}
          maxLength={80}
          data-testid="welcome-popup-title-input"
        />
      </div>

      <div>
        <label className="block text-xs font-medium text-slate-600 mb-1">Brödtext</label>
        <textarea
          value={cfg.body}
          onChange={(e) => update('body', e.target.value)}
          maxLength={200}
          rows={3}
          className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2a9d8f]/30"
          data-testid="welcome-popup-body-input"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Knapptext</label>
          <Input
            value={cfg.button_text}
            onChange={(e) => update('button_text', e.target.value)}
            maxLength={40}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Rabattkod-prefix</label>
          <Input
            value={cfg.code_prefix}
            onChange={(e) => update('code_prefix', e.target.value.toUpperCase().replace(/\s+/g, ''))}
            maxLength={20}
            placeholder="VALKOMMEN"
          />
          <p className="text-xs text-slate-400 mt-1">Koder genereras som t.ex. <span className="font-mono">{cfg.code_prefix}A1B2C3</span></p>
        </div>
      </div>

      <div className="flex justify-end pt-2 border-t">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[#2a9d8f] hover:bg-[#238b7e] text-white"
          data-testid="welcome-popup-save-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Sparar...' : 'Spara'}
        </Button>
      </div>
    </div>
  );
};

export default WelcomePopupSection;
